import { createContext, type PropsWithChildren, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { type PaletteMode, useMediaQuery } from '@mui/material'
import { useLocalStorage } from '@dhl/use-storage'

export type PreferredColorScheme = 'dark' | 'light' | 'system'

type ColorSchemeContextData = {
  activePaletteMode: PaletteMode
  preferredColorScheme: PreferredColorScheme
  setPreferredColorScheme: (mode: PreferredColorScheme) => void
  setPaletteMode: (mode: PaletteMode) => void
}

export const ColorSchemeContext = createContext<ColorSchemeContextData>({
  activePaletteMode: 'light',
  preferredColorScheme: 'light',
  setPreferredColorScheme: () => {},
  setPaletteMode: () => {},
})

const toMuiPaletteMode = (scheme: PreferredColorScheme): PaletteMode => (scheme === 'dark' ? 'dark' : 'light')

const paletteModeFromBool = (isDark: boolean): PaletteMode => (isDark ? 'dark' : 'light')

type Props = PropsWithChildren

export const ColorSchemeProvider = ({ children }: Props) => {
  /**
   * This is true when you set the preferences on browser level.
   * But also on system level, so macOs dark will also be the prefered in the media query!
   */
  const hasDarkModePreference = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true })

  const [preferredColorScheme, storePreferredColorScheme] = useLocalStorage<PreferredColorScheme>(
    'preferred-color-scheme',
    'light'
  )

  /**
   * Our initital active palette, which is something different then prefered palette
   */
  const initialPaletteMode =
    preferredColorScheme === 'system' && hasDarkModePreference ? 'dark' : toMuiPaletteMode(preferredColorScheme)

  const [activePaletteMode, setActivePaletteMode] = useState<PaletteMode>(initialPaletteMode)

  const setPreferredColorScheme = useCallback(
    (scheme: PreferredColorScheme) => {
      storePreferredColorScheme(scheme)

      let newPaletteMode = toMuiPaletteMode(scheme)

      if (scheme === 'system') {
        newPaletteMode = paletteModeFromBool(hasDarkModePreference)
      }

      setActivePaletteMode(newPaletteMode)
    },
    [hasDarkModePreference, storePreferredColorScheme]
  )

  // Update the active palette mode when the system appearance preference changes
  useEffect(() => {
    if (preferredColorScheme === 'system') {
      setActivePaletteMode(hasDarkModePreference ? 'dark' : 'light')
    }
  }, [hasDarkModePreference, preferredColorScheme, storePreferredColorScheme])

  const colorScheme: ColorSchemeContextData = useMemo(
    () => ({
      activePaletteMode,
      preferredColorScheme,
      setPreferredColorScheme,
      setPaletteMode: setActivePaletteMode,
    }),
    [activePaletteMode, preferredColorScheme, setPreferredColorScheme]
  )

  return <ColorSchemeContext.Provider value={colorScheme}>{children}</ColorSchemeContext.Provider>
}

export const useColorScheme = () => useContext(ColorSchemeContext)
